import { useCallback, useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import useAsyncValue from '../../../../hooks/useAsyncValue';
import { patch } from '../../../../redux/api';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import PropagateModal from '../../shared/SettingsPropagateModal';
import type { HiddenSnippetUrlsSettingsFields } from '../../../../../shared/settings/project/hiddenSnippetUrls';
import type { FailedFetchResponse } from '../../../../helpers/errors';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  endpoint: string;
};

const usePropagateExcludedSnippetRepositoriesSettings = ({ isOpen, onHide, endpoint }: Props) => {
  const addToast = useAddToast();
  const { setPending, setResolve, setReject, empty, isLoading, errors } = useAsyncValue<void>();
  const [isChecked, setIsChecked] = useState<boolean>(false);

  // Reset selection and clear any stale error from a prior failed propagate whenever the modal toggles.
  useEffect(() => {
    setIsChecked(false);
    empty();
  }, [isOpen, empty]);

  const onSavePropagateSettings = useCallback(async () => {
    try {
      setPending();
      const fields: HiddenSnippetUrlsSettingsFields[] = isChecked ? ['projectDefaultHiddenSnippetUrls'] : [];
      await patch<HiddenSnippetUrlsSettingsFields[]>(endpoint, fields);
      setResolve();
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      setReject(errorMessagesListFromResponse(error as FailedFetchResponse));
    }
  }, [addToast, endpoint, isChecked, onHide, setPending, setResolve, setReject]);

  return {
    isChecked,
    setIsChecked,
    onSavePropagateSettings,
    isLoading,
    errors,
    isSaveButtonDisabled: isLoading || !isChecked,
  };
};

/**
 * Lets an admin apply the org-default excluded snippet repositories to every project in the
 * organization, overwriting each project's existing list.
 */
export const PropagateExcludedSnippetRepositoriesModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
  endpoint,
}) => {
  const { errors, isLoading, isChecked, setIsChecked, isSaveButtonDisabled, onSavePropagateSettings } =
    usePropagateExcludedSnippetRepositoriesSettings({ isOpen, onHide, endpoint });

  return (
    <PropagateModal
      settingsCategory="excluded snippet repositories"
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={onSavePropagateSettings}
      isSaveButtonDisabled={isSaveButtonDisabled}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label="Apply excluded snippet repositories to all projects"
        isChecked={isChecked}
        onChange={(_event, checked) => setIsChecked(checked)}
        isRippleDisabled
      />
    </PropagateModal>
  );
};
